/**
 * Mapeador e Normalizador de Erros da Emissão de NFS-e (EasyGestor Frontend)
 * 
 * Converte retornos brutos da API Django, DRF, InvoiCy, SEFAZ e prefeituras em
 * diagnósticos claros em português, com orientações de correção e ações rápidas.
 */

export const KNOWN_ERROR_CODES = {
    // Permissões e limites de plano (HTTP 403 / backend permissions.py)
    notes_overflowed_limit: {
        title: "Limite de Notas Fiscais Atingido",
        description: "Você atingiu o limite mensal de emissão de notas fiscais contratado no seu plano.",
        hint: "Adquira um pacote de notas adicionais ou faça upgrade do seu plano para continuar emitindo.",
        action: "buy_notes",
        actionLabel: "Comprar Notas Adicionais",
    },
    plan_not_allowed: {
        title: "Plano Não Autorizado para Emissão",
        description: "O plano atual da sua empresa é de gestão e não inclui o módulo de emissão fiscal.",
        hint: "Faça upgrade para um plano emissor para liberar a emissão de NFS-e e NF-e.",
        action: "upgrade_plan",
        actionLabel: "Conhecer Planos Emissores",
        actionUrl: "/plan/branch-invoices",
    },
    pending_notes: {
        title: "Notas Fiscais Pendentes",
        description: "Existem notas fiscais em fila de processamento para esta filial.",
        hint: "Aguarde a conclusão das emissões anteriores antes de enviar uma nova nota.",
        action: "support",
    },

    // Regras de negócio e validações internas
    rps_pending: {
        title: "RPS em Processamento",
        description: "Já existe uma emissão em andamento para esta numeração de RPS.",
        hint: "Aguarde o retorno da prefeitura na listagem de notas antes de tentar novamente.",
    },
    rps_cancelled: {
        title: "RPS Já Cancelado",
        description: "A prefeitura informou que este RPS já se encontra cancelado.",
        hint: "Gere uma nova nota com a próxima numeração de RPS disponível.",
    },
    rps_number_used: {
        title: "Numeração de RPS Já Utilizada",
        description: "Este número de RPS já foi emitido anteriormente na prefeitura.",
        hint: "Acesse as Configurações de NFS-e e avance o número do próximo RPS.",
        action: "check_config",
        actionLabel: "Ajustar Numeração nas Configurações",
        actionUrl: "/nfse/config",
    },
    rps_duplicated: {
        title: "RPS Duplicado",
        description: "A prefeitura identificou que esta nota fiscal já foi transmitida.",
        hint: "Consulte a listagem de NFS-e para verificar a nota autorizada antes de reenviar.",
    },
    rps_denied: {
        title: "Emissão Rejeitada pela Prefeitura",
        description: "A prefeitura recusou os dados fiscais ou tributários informados.",
        hint: "Revise a atividade (CNAE), código de tributação, alíquota de ISS e endereço do tomador.",
    },
    other_error: {
        title: "Rejeição na Emissão",
        description: "A prefeitura ou o canal emissor retornou uma rejeição.",
        hint: "Revise as mensagens detalhadas abaixo ou contacte o suporte caso a dúvida persista.",
    },
};

export const FIELD_LABELS = {
    taker: "Tomador (Cliente)",
    service: "Serviço Prestado",
    rps: "RPS / Identificação",
    cpf_cnpj: "CPF/CNPJ",
    name: "Razão Social / Nome",
    original_id: "Cadastro de Cliente",
    value: "Valor do Serviço",
    cnae_code: "Atividade CNAE",
    item_list_service: "Item da Lei 116",
    service_code: "Código Municipal do Serviço",
    city_code_provider: "Município da Prestação",
    state_location: "Estado (UF)",
    city_location: "Cidade",
    postal_code: "CEP",
    street_number: "Número",
    address: "Endereço / Logradouro",
    district: "Bairro",
    nat_op: "Natureza da Operação",
    exigibilidade_iss: "Exigibilidade do ISS",
    aliq_iss: "Alíquota do ISS",
};

export const DEFAULT_MESSAGES = {
    prefectureError: "Não foi possível autorizar a NFS-e. A prefeitura retornou um erro.",
    invoicyError: "Não recebemos a confirmação do canal emissor (InvoiCy). Tente novamente em instantes.",
    invoicyHint: "Se a falha persistir, confira as configurações fiscais e o certificado digital da empresa.",
    networkError: "Falha de conexão com o servidor. Verifique sua internet e tente novamente.",
};

/**
 * Tenta fazer o parse de uma string JSON com segurança.
 */
export function tryParseJsonString(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return null;
    try {
        return JSON.parse(trimmed);
    } catch (e) {
        return null;
    }
}

/**
 * Extrai objeto JSON de strings ou payloads complexos
 */
export function coerceToObject(value) {
    if (!value) return null;
    if (typeof value === "object") return value;
    const direct = tryParseJsonString(value);
    if (direct) return direct;
    if (typeof value === "string") {
        const start = value.indexOf("{");
        const end = value.lastIndexOf("}");
        if (start !== -1 && end !== -1 && end > start) {
            const candidate = value.slice(start, end + 1);
            return tryParseJsonString(candidate);
        }
    }
    return null;
}

/**
 * Achata arrays e objetos aninhados em lista de strings
 */
export function flattenDetails(value, prefix = "") {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.map((item) => {
            if (typeof item === "object" && item !== null) {
                return flattenDetails(item, prefix).join("; ");
            }
            return prefix ? `${prefix}: ${String(item)}` : String(item);
        }).filter(Boolean);
    }
    if (typeof value === "object") {
        let list = [];
        for (const [k, v] of Object.entries(value)) {
            const fieldName = FIELD_LABELS[k] || k;
            const fullPrefix = prefix ? `${prefix} -> ${fieldName}` : fieldName;
            if (typeof v === "object" && v !== null) {
                list = list.concat(flattenDetails(v, fullPrefix));
            } else if (v !== null && v !== undefined && String(v).trim() !== "") {
                list.push(`${fullPrefix}: ${String(v)}`);
            }
        }
        return list.filter(Boolean);
    }
    if (typeof value === "string" || typeof value === "number") {
        return [prefix ? `${prefix}: ${String(value)}` : String(value)];
    }
    return [];
}

/**
 * Extrai o payload de erro de diferentes formatos de resposta (Axios, Fetch, DRF)
 */
export function parsePrefectureErrorPayload(error) {
    if (!error) return null;

    if (typeof error === "string") {
        return tryParseJsonString(error) || error;
    }

    if (typeof error === "object") {
        if (error.response) {
            const responseData = error.response.data !== undefined ? error.response.data : error.response;
            const parsed = typeof responseData === "string" ? tryParseJsonString(responseData) : null;
            return parsed || responseData;
        }
        if (error.data) {
            const parsed = typeof error.data === "string" ? tryParseJsonString(error.data) : null;
            return parsed || error.data;
        }
        return error;
    }

    return null;
}

/**
 * Função principal de normalização de erro da NFS-e
 */
export function normalizePrefectureError(error) {
    const fallback = {
        show: true,
        title: "Erro na emissão da NFS-e",
        description: DEFAULT_MESSAGES.prefectureError,
        code: (error && (error.status || (error.response && error.response.status))) || null,
        tag: null,
        details: [],
        action: null,
        actionLabel: null,
        actionUrl: null,
        rawResponse: "",
    };

    let payload = parsePrefectureErrorPayload(error);
    if (!payload) {
        if (error && error.message && error.message.includes("Network Error")) {
            return {
                ...fallback,
                title: "Erro de Conexão",
                description: DEFAULT_MESSAGES.networkError,
            };
        }
        return fallback;
    }

    // Se o payload for uma string direta
    if (typeof payload === "string") {
        const parsed = coerceToObject(payload);
        if (parsed) {
            payload = parsed;
        } else {
            // Verificar se a string contém erros conhecidos
            if (payload.includes("number of notes has been exceeded") || payload.includes("notes_overflowed_limit")) {
                return {
                    ...fallback,
                    ...KNOWN_ERROR_CODES.notes_overflowed_limit,
                    tag: "notes_overflowed_limit",
                };
            }
            if (payload.includes("not allowed for current plan") || payload.includes("plan_not_allowed")) {
                return {
                    ...fallback,
                    ...KNOWN_ERROR_CODES.plan_not_allowed,
                    tag: "plan_not_allowed",
                };
            }
            return {
                ...fallback,
                description: payload || fallback.description,
            };
        }
    }

    // 1. Verificar chaves diretas de permissão/plano (ex: { notes_overflowed_limit: "..." })
    if (payload.notes_overflowed_limit || (typeof payload === "object" && Object.keys(payload).includes("notes_overflowed_limit"))) {
        return {
            ...fallback,
            ...KNOWN_ERROR_CODES.notes_overflowed_limit,
            code: 403,
            tag: "notes_overflowed_limit",
            details: [
                "O pacote de notas do mês encerrou.",
                "Clique no botão abaixo para adicionar notas extras e liberar a emissão imediata."
            ],
        };
    }

    if (payload.plan_not_allowed || (typeof payload === "object" && Object.keys(payload).includes("plan_not_allowed"))) {
        return {
            ...fallback,
            ...KNOWN_ERROR_CODES.plan_not_allowed,
            code: 403,
            tag: "plan_not_allowed",
            details: [
                "O plano configurado para esta filial não possui o módulo emissor contratado."
            ],
        };
    }

    if (payload.pending_notes || (typeof payload === "object" && Object.keys(payload).includes("pending_notes"))) {
        return {
            ...fallback,
            ...KNOWN_ERROR_CODES.pending_notes,
            code: 403,
            tag: "pending_notes",
        };
    }

    // 2. Extrair nó de dados principal (payload.error, payload.data, ou payload direto)
    let data = payload.error || payload.data || payload;
    if (typeof data === "string") {
        const parsed = coerceToObject(data);
        if (parsed) data = parsed;
    }

    // Casos especiais de string em data.error
    if (typeof payload.error === "string") {
        const errStr = payload.error;
        if (errStr.includes("taker undefined") || errStr.includes("cpf_cnpj or original_id required")) {
            return {
                ...fallback,
                title: "Tomador Incompleto",
                description: "Os dados do tomador (cliente) estão incompletos ou não foram cadastrados.",
                hint: "Verifique o CPF/CNPJ e certifique-se de que o tomador está cadastrado como cliente.",
                details: ["O tomador precisa de CPF/CNPJ válido e cadastro salvo na base de dados."],
            };
        }
        if (errStr.includes("Nenhum documento ou texto fornecido")) {
            return {
                ...fallback,
                title: "Documento Não Fornecido",
                description: "Nenhum arquivo ou texto foi identificado para processamento.",
            };
        }
        if (errStr.includes("Dados do tomador e branch_owner são obrigatórios")) {
            return {
                ...fallback,
                title: "Dados Obrigatórios Ausentes",
                description: "Não foi possível identificar a filial ou os dados do tomador do serviço.",
            };
        }
    }

    if (!data || typeof data !== "object") {
        return {
            ...fallback,
            description: typeof data === "string" ? data : fallback.description,
        };
    }

    let tag = data.tag || (typeof payload.tag === "string" ? payload.tag : null);
    let code = data.code || payload.code || fallback.code;

    // Se o código for 313 (RPS já efetivado no InvoiCy)
    if (code === 313 || String(code) === "313" || (typeof data.description === "string" && data.description.includes("313"))) {
        return {
            ...fallback,
            title: "RPS Já Efetivado na Prefeitura",
            description: "Este RPS já foi transmitido e autorizado anteriormente pelo sistema.",
            code: 313,
            tag: "rps_duplicated",
            hint: "Consulte a listagem de NFS-e para acessar o PDF/XML ou avance a numeração do RPS nas configurações.",
            action: "check_config",
            actionLabel: "Ajustar Numeração do RPS",
            actionUrl: "/nfse/config",
            details: [
                "A numeração deste RPS já consta como gerada na base da prefeitura/InvoiCy.",
                "Se deseja emitir uma nova nota, aumente o número do RPS nas Configurações de NFS-e."
            ]
        };
    }

    // Extrair descrição base
    let baseDescription = data.description || data.detail || payload.detail;
    const nestedObj = typeof baseDescription === "string" ? coerceToObject(baseDescription) : null;
    if (nestedObj && typeof nestedObj === "object") {
        baseDescription = nestedObj.description || nestedObj.detail || baseDescription;
        tag = tag || nestedObj.tag || null;
        code = nestedObj.code || code;
        data = {
            ...data,
            details: data.details || nestedObj.details || [],
            errors: data.errors || nestedObj.errors || [],
        };
    }

    // Mapeamento por tag conhecida
    const knownConfig = tag && KNOWN_ERROR_CODES[tag] ? KNOWN_ERROR_CODES[tag] : null;

    let title = (knownConfig && knownConfig.title) || "Erro na emissão da NFS-e";
    let description = (knownConfig && knownConfig.description) || baseDescription || DEFAULT_MESSAGES.prefectureError;
    let hint = (knownConfig && knownConfig.hint) || null;
    let action = (knownConfig && knownConfig.action) || null;
    let actionLabel = (knownConfig && knownConfig.actionLabel) || null;
    let actionUrl = (knownConfig && knownConfig.actionUrl) || null;

    const detailsSet = new Set();

    // Inspecionar erros de validação de campos (ex: { taker: { cpf_cnpj: ["..."] } })
    ["taker", "service", "rps", "errors", "non_field_errors", "detail", "details"].forEach((field) => {
        if (data[field]) {
            const flattened = flattenDetails(data[field], FIELD_LABELS[field] || field);
            flattened.forEach((item) => detailsSet.add(item));
        }
    });

    // Tratar respostas com formato da prefeitura / InvoiCy ("Código: ..., Mensagem: ... Correção: ...")
    if (typeof baseDescription === "string") {
        const isUnexpectedInvoicy = baseDescription.toLowerCase().includes("resposta inesperada da invoicy");
        if (isUnexpectedInvoicy) {
            title = "Instabilidade no Canal Emissor";
            description = DEFAULT_MESSAGES.invoicyError;
            hint = DEFAULT_MESSAGES.invoicyHint;
            const cleaned = baseDescription.replace(/.*Resposta inesperada da InvoiCy:/i, "").trim();
            if (cleaned) {
                detailsSet.add("Resposta técnica: " + cleaned.replace(/[{}]/g, "").trim());
            }
        } else {
            // Regex para capturar Código e Mensagem (com ou sem acentuação)
            const codeMatches = baseDescription.match(/C[oó]digo:\s*[^,]+,\s*Mensagem:[^\.]+(?:\.|$)/gi);
            if (codeMatches && codeMatches.length) {
                codeMatches.forEach((m) => detailsSet.add(m.replace(/\s+/g, " ").trim()));
                const firstIndex = baseDescription.indexOf(codeMatches[0]);
                if (firstIndex > 0) {
                    const summary = baseDescription.slice(0, firstIndex).trim();
                    if (summary) description = summary;
                }
            } else {
                // Fallback para códigos avulsos no texto (ex: Codigo: E145 ou Código: 145)
                const singleCodeMatches = baseDescription.match(/C[oó]digo:\s*[^,\n\.]+/gi);
                if (singleCodeMatches) {
                    singleCodeMatches.forEach((m) => detailsSet.add(m.trim()));
                }
                const msgMatches = baseDescription.match(/Mensagem:\s*[^,\n\.]+/gi);
                if (msgMatches) {
                    msgMatches.forEach((m) => detailsSet.add(m.trim()));
                }
            }

            // Regex para capturar Correção recomendada (com ou sem acentuação)
            const corrMatches = baseDescription.match(/Corre[cç][aã]o:[^\.]+(?:\.|$)/gi);
            if (corrMatches && corrMatches.length) {
                corrMatches.forEach((m) => detailsSet.add(m.trim()));
            }

            // Regex para mensagens InvoiCy com rejeição estruturada
            if (baseDescription.includes("Documento rejeitado") || baseDescription.includes("Falha na estrutura enviada")) {
                title = "Documento Rejeitado";
            }
        }
    }

    if (hint) {
        detailsSet.add(`💡 Dica: ${hint}`);
    }

    return {
        show: true,
        title,
        description,
        code,
        tag,
        details: Array.from(detailsSet).filter(Boolean),
        action,
        actionLabel,
        actionUrl,
        rawResponse: "",
    };
}



// WEBPACK FOOTER //
// ./src/views/Fiscal/NFSe/emit/utils/errorHandler.js